import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { api, openBooking } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline } from "../components/site/Primitives";

const EASE = [0.16, 1, 0.3, 1];

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <PageHero
        overline="Luxury Foot Wellness"
        title="A spa experience for hands and feet."
        subtitle="Luxury pedicures, foot wellness, and natural nail care delivered with elegance, comfort, and health-aware attention to every detail."
      />

      <section className="bg-brand-cream py-24 md:py-32" data-testid="services-list">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 space-y-6">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
              className={`grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-brand-ink/10 bg-white ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="relative h-72 md:h-auto overflow-hidden group">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                <span className="absolute top-5 left-5 bg-brand-ink/85 text-brand-cream text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-full backdrop-blur">{s.category}</span>
              </div>
              <div className="p-9 md:p-12 flex flex-col justify-center">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif text-3xl md:text-4xl text-brand-ink tracking-tight">{s.title}</h3>
                  <span className="font-serif text-brand-rose text-2xl whitespace-nowrap">{s.price}</span>
                </div>
                <p className="text-brand-ink/70 font-light mt-4 leading-relaxed">{s.description}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2.5 text-sm text-brand-ink/80"><Check size={15} className="text-brand-gold shrink-0" />{h}</li>
                  ))}
                </ul>
                <div className="mt-8 flex items-center gap-6">
                  {s.duration && <span className="text-xs uppercase tracking-[0.2em] text-brand-ink/50">{s.duration}</span>}
                  <button onClick={openBooking} data-testid={`service-book-${s.id}`} className="btn-fill bg-brand-ink text-brand-cream rounded-full px-7 py-3 text-sm font-medium inline-flex items-center gap-2">Book on Vagaro <ArrowUpRight size={16} /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-brand-pink/50 py-20 md:py-28" data-testid="services-cta">
        <div className="max-w-3xl mx-auto px-6 text-center flex flex-col items-center">
          <Overline className="mb-5">Ready to be pampered?</Overline>
          <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Book your luxury experience today.</h2>
          <button onClick={openBooking} data-testid="services-book-cta" className="btn-fill bg-brand-ink text-brand-cream rounded-full px-9 py-4 text-sm font-medium mt-8">Book an Appointment</button>
        </div>
      </section>
    </div>
  );
}
